/**
 * canonSectionsCore — pure functions behind scripts/buildCanonSections.ts.
 * Split out for unit testing (shared with the client retriever).
 */

export interface CanonSection {
  /** Stable ref: "<file>#<heading-slug>" — citable in AI replies. */
  ref: string;
  doc: string;
  docTitle: string;
  heading: string;
  /** Section text, capped; `truncated` marks the cut. */
  text: string;
  truncated: boolean;
}

export const SECTION_TEXT_CAP = 1400;

export function slugify(heading: string): string {
  return heading
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80);
}

/**
 * Split a canon markdown document into sections on `##`/`###` headings.
 * The preamble before the first heading becomes the doc's own section.
 */
export function splitMarkdownSections(doc: string, content: string): CanonSection[] {
  const lines = content.split('\n');
  const docTitle = lines.find((l) => l.startsWith('# '))?.slice(2).trim() ?? doc;
  const sections: CanonSection[] = [];
  let heading = '(overview)';
  let buf: string[] = [];

  const flush = () => {
    const text = buf.join('\n').trim();
    if (text.length < 40) { buf = []; return; } // skip empty/trivial chunks
    const capped = text.length > SECTION_TEXT_CAP;
    sections.push({
      ref: `${doc}#${slugify(heading)}`,
      doc,
      docTitle,
      heading,
      text: capped ? `${text.slice(0, SECTION_TEXT_CAP)}…` : text,
      truncated: capped,
    });
    buf = [];
  };

  for (const line of lines) {
    const m = line.match(/^(##|###) (.+)$/);
    if (m) {
      flush();
      heading = m[2].trim();
    } else {
      buf.push(line);
    }
  }
  flush();
  return sections;
}

/** Simple deterministic keyword scorer shared with the client retriever. */
export function scoreSection(queryTokens: string[], section: CanonSection): number {
  if (queryTokens.length === 0) return 0;
  const hay = `${section.docTitle} ${section.heading}`.toLowerCase();
  const body = section.text.toLowerCase();
  let score = 0;
  for (const t of queryTokens) {
    if (hay.includes(t)) score += 3;
    if (body.includes(t)) score += 1;
  }
  return score;
}

export function tokenizeQuery(query: string): string[] {
  return Array.from(
    new Set(
      query
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, ' ')
        .split(/\s+/)
        .filter((t) => t.length > 2 && !STOP_WORDS.has(t)),
    ),
  );
}

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'are', 'can', 'how', 'what', 'why', 'who', 'does', 'this',
  'that', 'with', 'from', 'into', 'about', 'when', 'where', 'which', 'should',
  'would', 'could', 'you', 'your', 'our', 'them', 'they', 'has', 'have', 'had',
  'not', 'but', 'was', 'were', 'will', 'its', 'get', 'use', 'one', 'all',
]);
